import { useReducedMotion } from 'framer-motion';

/**
 * The dashboard's motion vocabulary, in one place.
 *
 * Every animated surface pulls its variants from here, which buys two things:
 * the whole account area moves on the same timing curve instead of each page
 * inventing its own, and "respect prefers-reduced-motion" stays a single
 * decision rather than a per-component one somebody will eventually forget.
 *
 * Reduced motion does not mean *no* feedback — opacity still resolves, so the
 * interface never looks frozen. Only the travel and scale are dropped, since
 * those are what trigger vestibular discomfort.
 */

// Gentle deceleration — fast to start, settles softly. Reads as "premium"
// where a linear or bouncy curve reads as "template".
const EASE = [0.22, 1, 0.36, 1];

export function useMotionPresets() {
  const reduced = useReducedMotion();

  return {
    /** Section entrance. Pair with `stagger` on the parent. */
    fadeUp: {
      hidden: { opacity: 0, y: reduced ? 0 : 12 },
      show: {
        opacity: 1,
        y: 0,
        transition: { duration: reduced ? 0.15 : 0.4, ease: EASE },
      },
    },

    /** Parent container that releases its children in sequence. */
    stagger: {
      hidden: {},
      show: {
        transition: {
          staggerChildren: reduced ? 0 : 0.05,
          delayChildren: reduced ? 0 : 0.04,
        },
      },
    },

    /** For things that appear in place — modals, badges, success ticks. */
    scaleIn: {
      hidden: { opacity: 0, scale: reduced ? 1 : 0.96 },
      show: {
        opacity: 1,
        scale: 1,
        transition: { duration: reduced ? 0.15 : 0.3, ease: EASE },
      },
    },

    /** Card hover. `undefined` (not `{}`) so framer skips the gesture entirely. */
    hoverLift: reduced ? undefined : { y: -3, transition: { duration: 0.2, ease: EASE } },

    /** Press feedback on buttons and nav rows. */
    tap: reduced ? undefined : { scale: 0.985 },

    reduced,
  };
}
